import { useCallback, useEffect, useRef, useState } from "react";
import {
    fetchUserDirectory,
    type UserDirectoryItem,
} from "@/lib/axios/api/user-directory.api";

interface PaginationState {
    pstn: { page: number; limit: number };
    teams: { page: number; limit: number };
}

interface UseAgentDirectoryDataReturn {
    allAgents: UserDirectoryItem[];
    pstnUsers: UserDirectoryItem[];
    teamsUsers: UserDirectoryItem[];
    totalPstnCount: number;
    totalTeamsCount: number;
    pstnLoading: boolean;
    teamsLoading: boolean;
    loading: boolean;
    error: string | null;
    pagination: PaginationState;
    setPstnPage: (page: number) => void;
    setTeamsPage: (page: number) => void;
    refetch: () => Promise<void>;
}

const PAGE_SIZE = 5;

export function useAgentDirectoryData(params?: {
    startDate?: string | null;
    endDate?: string | null;
    agentMicrosoftIds?: string[] | null;
}): UseAgentDirectoryDataReturn {
    const { startDate, endDate, agentMicrosoftIds } = params ?? {};
    const [teamsUsers, setTeamsUsers] = useState<UserDirectoryItem[]>([]);
    const [pstnUsers, setPstnUsers] = useState<UserDirectoryItem[]>([]);
    const [totalPstnCount, setTotalPstnCount] = useState(0);
    const [totalTeamsCount, setTotalTeamsCount] = useState(0);
    const [pstnLoading, setPstnLoading] = useState(false);
    const [teamsLoading, setTeamsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState<PaginationState>({
        pstn: { page: 1, limit: PAGE_SIZE },
        teams: { page: 1, limit: PAGE_SIZE },
    });

    // Reset to page 1 when filter params change
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setPagination((prev) => ({
            pstn: { ...prev.pstn, page: 1 },
            teams: { ...prev.teams, page: 1 },
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, JSON.stringify(agentMicrosoftIds)]);

    const fetchTeamsUsers = useCallback(async (signal?: AbortSignal) => {
        try {
            setTeamsLoading(true);
            setError(null);

            const teamsResponse = await fetchUserDirectory(
                {
                    userName: undefined,
                    departments: [],
                    page: pagination.teams.page,
                    pageSize: pagination.teams.limit,
                    startTime: startDate,
                    endTime: endDate,
                    agentIds: agentMicrosoftIds,
                },
                { signal }
            );

            setTeamsUsers(teamsResponse.items ?? []);
            setTotalTeamsCount(teamsResponse.totalCount ?? 0);
        } catch (err) {
            if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
                setError(
                    err instanceof Error ? err.message : "Failed to fetch agent directory"
                );
                setTeamsUsers([]);
                setTotalTeamsCount(0);
            }
        } finally {
            setTeamsLoading(false);
        }
    }, [pagination.teams.limit, pagination.teams.page, startDate, endDate, agentMicrosoftIds]);

    const fetchPstnUsers = useCallback(async (signal?: AbortSignal) => {
        try {
            setPstnLoading(true);
            setError(null);

            const pstnResponse = await fetchUserDirectory(
                {
                    userName: undefined,
                    departments: [],
                    isPstnUser: true,
                    page: pagination.pstn.page,
                    pageSize: pagination.pstn.limit,
                    startTime: startDate,
                    endTime: endDate,
                    agentIds: agentMicrosoftIds,
                },
                { signal }
            );

            setPstnUsers(pstnResponse.items ?? []);
            setTotalPstnCount(pstnResponse.totalCount ?? 0);
        } catch (err) {
            if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
                setError(
                    err instanceof Error ? err.message : "Failed to fetch agent directory"
                );
                setPstnUsers([]);
                setTotalPstnCount(0);
            }
        } finally {
            setPstnLoading(false);
        }
    }, [pagination.pstn.limit, pagination.pstn.page, startDate, endDate, agentMicrosoftIds]);

    const fetchAllAgents = useCallback(async () => {
        await Promise.all([fetchTeamsUsers(), fetchPstnUsers()]);
    }, [fetchPstnUsers, fetchTeamsUsers]);

    useEffect(() => {
        const controller = new AbortController();
        fetchTeamsUsers(controller.signal);

        return () => controller.abort();
    }, [fetchTeamsUsers]);

    useEffect(() => {
        const controller = new AbortController();
        fetchPstnUsers(controller.signal);

        return () => controller.abort();
    }, [fetchPstnUsers]);

    return {
        allAgents: teamsUsers,
        pstnUsers,
        teamsUsers,
        totalPstnCount,
        totalTeamsCount,
        pstnLoading,
        teamsLoading,
        loading: pstnLoading || teamsLoading,
        error,
        pagination,
        setPstnPage: (page: number) =>
            setPagination((prev) => ({
                ...prev,
                pstn: { ...prev.pstn, page },
            })),
        setTeamsPage: (page: number) =>
            setPagination((prev) => ({
                ...prev,
                teams: { ...prev.teams, page },
            })),
        refetch: fetchAllAgents,
    };
}
